import { OperationRoleName } from "./networkRequest";

const currentYear: number = new Date().getFullYear();

export const COPY = {
  titles: {
    activity: 'Activity',
    checkpoints: 'Checkpoints',
    home: 'Home',
    officialVerifications: 'Verifications',
    operations: 'Operations',
    settings: 'Settings',
    surepassAdmin: 'SurePass Admin',
    users: 'Users',
  },
  copyrightFooter: `© ${currentYear}, SurePass ID Inc.`,
  publicRegistration: {
    success: 'Congratulations! Your SurePass account has been successfully created. Please check your inbox for further instructions. Note that additional security checks may be required.',
    failure: 'Sorry, we are unable to verify your credentials. Please contact support for assistance at support@surepass.com'
  },
  imageUpload: {
    fileType: 'File must be PNG, JPG, or HEIC (max. 10MB)',
    step1: 'You must be the only person in the photo.',
    step2: 'Include and center your full head and shoulders only.',
    step3: 'Your face must be clearly visible and facing the camera.',
  },
  logoUpload: {
    fileType: 'Images must be either a JPG or PNG file.',
    size: 'Image files must not exceed 5MB in size.',
    background: 'Use white or transparent backgrounds for best results.'
  },
  fallback: {
    header: 'Something went wrong.',
    fullAppExplanation: 'Uh-oh, looks like there was an error.',
    pageExplanation: 'Uh-oh, looks like there was an error. Try again or navigate elsewhere in the app.',
    pageButton: 'Go Back',
    fullAppButton: 'Retry',
    fullAppFallbackContact: 'If the problem persists, please email us at ',
    supportEmail: 'support@surepass.com'
  },
  notFound: {
    header: '404 - Not Found',
    explanation: "Uh-oh, looks like this page has gone AWOL. It's either undercover or you don't have permission to access it.",
    nonAdminExplanation: "Uh-oh, looks like this page has gone AWOL. It's either undercover or you don't have permission to access it.",
    button1: 'Go Back',
    button2: 'Back to Sign-in',
    tag: 'In the meantime, enjoy this photo of a police sports car!'
  },
  agencyInfo: {
    domain: 'This email will be used for account verification of agency staff.'
  },
  landingPage: {
    officialsCleared: 'Total Officials Cleared',
    armedOfficialsCleared: 'Armed Officials Cleared',
    officialsNotCleared: 'Officials Not Cleared',
  }
};

export const HELP_SCOUT_URLS = {
  userManagement: 'https://surepass.helpscoutdocs.com/category/99-user-management',
};

// Be very careful changing these, they are using to define the App Routes in App.tsx
export const PAGE_PATHS = {
  activity: '/activity',
  checkpoints: '/checkpoints',
  home: '/',
  verifications: '/official-verifications',
  operations: '/operations',
  settings: '/settings',
  surepassAdmin: '/surepass-admin',
  users: '/users',
  usersImport: '/users/import',
  register: '/register',
};

export const ROLES = {
  ADMIN: 'Admin',
  OWNER: 'Owner',
  SUPER_ADMIN: 'Super Admin',
  OFFICIAL: 'Official',
  VERIFIER: 'Verifier',
  VERIFICATION_SPECIALIST: 'Verification Specialist',
  AGENCY_VERIFIER: 'Agency Verifier',
};

export const STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming'
];

// Feature Flag Names
export const FEATURE_FLAGS = {
  OPERATIONS: 'OPERATIONS',
  VERIFICATIONS: 'VERIFICATIONS',
  WEB_QUICK_SCAN: 'WEB_QUICK_SCAN'
} as const;

// Operation Agency Official Types
export const OPERATION_AGENCY_OFFICIAL_ROLES: { [key: string]: OperationRoleName } = {
  INCIDENT_COMMANDER: 'Incident Commander',
  DEPUTY_INCIDENT_COMMANDER: 'Deputy Incident Commander',
  COMMAND_STAFF: 'Command Staff',
  PARTICIPANT: 'Participant'
};

// Operation Incident Commander Assignments
export const OPERATION_INCIDENT_COMMANDER_ASSIGNMENTS = [
  'Incident Commander',
  'Deputy Incident Commander',
]
// Operation Command Staff Assignments
export const OPERATION_COMMAND_STAFF_ASSIGNMENTS = [
  'Public Information Officer',
  'Safety Officer',
  'Liaison Officer',
  'Operations Section Chief',
  'Planning Section Chief',
  'Logistics Section Chief',
  'Finance/Administration Section Chief'
];

// Operation Participant Assignments
export const OPERATION_PARTICIPANT_ASSIGNMENTS = [
  'Field Officer',
  'Support Staff',
  'Medical Team',
  'Logistics Coordinator',
  'Communications Specialist'
];

export const OPERATION_INCIDENT_TYPES = {
  show_of_force: 'Show of Force',
  use_of_force: 'Use of Force',
  arrest: 'Arrest',
  citizen_contact: 'Citizen Contact',
  medical_deployed: 'Medical Deployed',
  fire_deployed: 'Fire Deployed',
  property_damaged: 'Property Damaged',
  counter_measures_deployed: 'Counter Measures Deployed',
  verbal_warning: 'Verbal Warning'
} as const;

export const OPERATION_INCIDENT_STATUSES = {
  ongoing: 'Ongoing',
  closed: 'Closed',
} as const;